import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardBody, Container, Input, Spinner } from 'reactstrap';
import { FaArrowLeft, FaPrint, FaSearch, FaSyncAlt, FaUser } from 'react-icons/fa';
import POSHeader from './POSHeader';
import POSKeypadModal from './POSKeypadModal';
import clientService from '../../services/clientService';
import { customerBalance, customerBalanceLabel, customerName, printCustomerSituation } from '../../helpers/customerSituationTicket';
import { formatPrice } from '../../helpers/utility';
import { POS_PAGE, RETOUR_FORM_NEW } from '../../routes/routeConstants';
import './CustomerSituationPOS.css';

export default function CustomerSituationPOS() {
  const auth = useSelector(state => state.auth);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [clients, setClients] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [printError, setPrintError] = useState(null);
  const [printingId, setPrintingId] = useState(null);
  const printInProgress = useRef(false);
  const [refresh, setRefresh] = useState(0);
  const [keypadOpen, setKeypadOpen] = useState(false);

  const changeSearch = value => {
    setQuery(value);
    setPage(0);
    setLoading(true);
    setPrintError(null);
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    const timer = setTimeout(async () => {
      try {
        const response = await clientService.findAll({ query: query.trim(), page, size: 24 });
        if (!active) return;
        const result = response.data.body;
        if (page > 0 && page >= result.totalPages) {
          setPage(Math.max(0, result.totalPages - 1));
          return;
        }
        setClients(result.content || []);
        setTotalPages(result.totalPages || 0);
        setTotalElements(result.totalElements ?? result.content?.length ?? 0);
      } catch {
        if (active) setError('Impossible de charger les clients.');
      } finally {
        if (active) setLoading(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [query, page, refresh]);

  const print = async client => {
    if (printInProgress.current) return;
    printInProgress.current = true;
    setPrintingId(client.id);
    setPrintError(null);
    try {
      const response = await clientService.getClientById(client.id);
      const latest = response.data.body;
      if (!latest) throw new Error('Compte indisponible');
      setClients(current => current.map(row => row.id === client.id ? latest : row));
      await printCustomerSituation(latest, auth.businessName, 80);
    } catch {
      setPrintError({ id: client.id, message: 'Impossible de préparer la situation. Réessayez avec le bouton Imprimer.' });
    } finally {
      printInProgress.current = false;
      setPrintingId(null);
    }
  };

  return <Container fluid className="customer-situation-pos">
    <POSHeader {...auth} isAdmin={auth.role === 'ROLE_ADMIN'} activeTab="CLIENTS"
      onChangeTab={tab => navigate(tab === 'RETOURS' ? RETOUR_FORM_NEW : POS_PAGE)} />
    <div className="customer-situation-heading">
      <h1 className="h5 mb-0"><FaUser className="me-2" />Situation des clients</h1>
      <Button size="sm" color="light" onClick={() => navigate(POS_PAGE)}><FaArrowLeft className="me-2" />Retour aux ventes</Button>
    </div>
    <Card><CardBody>
      <div className="customer-situation-toolbar">
        <div className="customer-situation-search">
          <label htmlFor="situation-customer-search" className="form-label fw-bold"><FaSearch className="me-2" />Chercher un client</label>
          <Input id="situation-customer-search" type="search" placeholder="Nom ou code du client"
            value={query} readOnly inputMode="none" aria-haspopup="dialog"
            onClick={() => setKeypadOpen(true)}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setKeypadOpen(true);
              }
            }} />
        </div>
        <Button color="light" disabled={loading} onClick={() => { setPrintError(null); setRefresh(value => value + 1); }}><FaSyncAlt className="me-2" />Actualiser</Button>
      </div>
      {loading ? <p role="status"><Spinner tag="span" size="sm" /> Chargement des clients…</p>
        : error ? <div role="alert" className="alert alert-danger">{error} <Button onClick={() => setRefresh(value => value + 1)}>Réessayer</Button></div>
          : <>
            <div className="customer-situation-columns" aria-hidden="true"><span>Client</span><span>Solde du client</span><span>Situation</span></div>
            <ul className="customer-situation-list" aria-label="Clients et soldes">
              {clients.map(client => {
                const balance = customerBalance(client);
                return <li key={client.id} className="customer-situation-row">
                  <div className="customer-situation-name"><strong>{customerName(client)}</strong><small>{[client.code, client.telephone1].filter(Boolean).join(' · ')}</small></div>
                  <div className={`customer-situation-balance ${balance > 0 ? 'owed' : 'settled'}`}>
                    <strong>{formatPrice(Math.abs(balance))}</strong><span>{customerBalanceLabel(balance)}</span>
                  </div>
                  <div className="customer-situation-print">
                    <Button color="primary" disabled={printingId !== null} onClick={() => print(client)} aria-label={`Imprimer la situation de ${customerName(client)}`}>
                      {printingId === client.id ? <><Spinner tag="span" size="sm" className="me-2" />Préparation…</> : <><FaPrint className="me-2" />Imprimer</>}
                    </Button>
                    {printError?.id === client.id && <p role="alert" className="text-danger mt-2 mb-0">{printError.message}</p>}
                  </div>
                </li>;
              })}
            </ul>
            {!clients.length && <p className="text-center py-4">Aucun client trouvé.</p>}
            <div className="customer-situation-pagination">
              <span className="text-muted">{totalElements} client(s)</span>
              {totalPages > 1 && <nav className="d-flex align-items-center gap-2" aria-label="Pages des clients">
                <Button disabled={page === 0} onClick={() => setPage(page - 1)}>Précédent</Button>
                <span>{page + 1} / {totalPages}</span>
                <Button disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)}>Suivant</Button>
              </nav>}
            </div>
          </>}
    </CardBody></Card>
    <POSKeypadModal isOpen={keypadOpen} toggle={() => setKeypadOpen(false)}
      value={query} onChange={changeSearch} onSearch={() => setKeypadOpen(false)}
      title="Chercher un client" />
  </Container>;
}
